'use client'; // Error boundaries must be Client Components

import { useEffect } from 'react';
import { Box, Button, GridLayout, Typography } from '@wishket/design-system';
import Image from 'next/image';
import { usePathname, useRouter } from 'next/navigation';
import { twJoin } from 'tailwind-merge';

import { PATH } from '@/shared/constants';
import { useDeviceCheck } from '@/shared/hooks';

interface ErrorDataProps {
  img: string;
  title: string;
  description: string;
}
type ErrorCode = '404' | '500';

export default function Error({
  error,
}: {
  error: Error & { digest?: string };
}) {
  const pathname = usePathname();
  const isDetailPage = pathname?.includes(PATH.DETAIL);
  const { isDesktop } = useDeviceCheck();
  const router = useRouter();
  const errorCode: ErrorCode =
    error.message === '404' || error.message === '401' ? '404' : '500';
  const errorData: Record<ErrorCode, ErrorDataProps> = {
    '404': {
      img: '404.png',
      title: '페이지를 찾을 수 없습니다.',
      description: `페이지가 존재하지 않거나, 사용할 수 없는 페이지입니다.\n입력하신 주소가 정확한지 다시 한번 확인해 주세요.`,
    },
    '500': {
      img: '500.png',
      title: '내부 서버 오류',
      description: `내부 서버에 오류가 발생했습니다.\n서버에 발생한 오류를 최대한 빨리 해결하도록 하겠습니다.\n불편을 끼쳐드려 죄송합니다.`,
    },
  };

  useEffect(() => {
    console.error(
      JSON.stringify({
        'error-log': {
          code: error.message || 'UNKNOWN',
          constructor: error.constructor?.name,
          digest: error.digest,
          message: '글로벌 에러 페이지 노출',
          level: 'error',
          extra: {
            path: pathname,
            isDetailPage,
            stack: error.stack,
            name: error.name,
            cause: error.cause,
          },
        },
      })
    );
  }, [error, pathname, isDetailPage]);

  return (
    <GridLayout
      gridTemplateAreaClass={twJoin(
        'grid-areas-error-mobile desktop:grid-areas-error-desktop gap-y-8 mt-[149px] w-full min-h-[calc(100vh-666px)]',
        isDesktop ? 'py-[120px]' : 'py-12',
        isDetailPage && 'mt-0'
      )}
    >
      <Box className="flex w-full items-center justify-center grid-in-code">
        <Image
          src={`${process.env.NEXT_PUBLIC_APP_BASE_MEDIA_URL}/images/yozm/${errorData[errorCode].img}`}
          alt="error-code"
          width={0}
          height={0}
          unoptimized
          sizes="360px"
          className={twJoin(
            'object-contain',
            isDesktop ? 'h-[240px] w-[360px]' : 'h-[100px] w-[228px]'
          )}
        />
      </Box>
      <Box
        className={twJoin(
          'flex flex-col justify-center gap-y-3 grid-in-description',
          isDesktop ? 'items-start' : 'items-center'
        )}
      >
        <Typography variant="title2" className="font-medium text-w-gray-900">
          {errorData[errorCode].title}
        </Typography>
        <Typography
          variant="body1"
          className={twJoin(
            'whitespace-pre-line text-w-gray-700',
            isDesktop ? 'text-start' : 'text-center'
          )}
        >
          {errorData[errorCode].description}
        </Typography>
      </Box>
      <Box
        className={twJoin(
          'flex grid-in-cta',
          isDesktop
            ? 'flex-row-reverse justify-end gap-x-4'
            : 'flex-col gap-y-4'
        )}
      >
        <Button
          onClick={() => router.push(PATH.HOME)}
          variant="solid"
          type="button"
          size="md"
          className={isDesktop ? 'w-[220px]' : 'w-full'}
        >
          메인으로 돌아가기
        </Button>
        <Button
          onClick={() => router.back()}
          variant="outlined"
          type="button"
          size="md"
          className={isDesktop ? 'w-[220px]' : 'w-full'}
        >
          이전 페이지로 돌아가기
        </Button>
      </Box>
    </GridLayout>
  );
}
